// Cart persistence service for localStorage management
import type { CartItem, ServiceType } from '../types';

interface PersistedCartData {
  items: CartItem[];
  service_type: ServiceType;
  selected_location_id: string | null;
  total: number;
  lastUpdated: number;
}

const CART_STORAGE_KEY = 'tyrewarehouse_cart';
const CART_EXPIRY_DAYS = 30; // Cart expires after 30 days

export const cartPersistenceService = {
  /**
   * Save cart data to localStorage
   */
  saveCart: (cartData: Omit<PersistedCartData, 'lastUpdated'>): void => {
    try {
      const dataToSave: PersistedCartData = {
        ...cartData,
        lastUpdated: Date.now()
      };
      
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(dataToSave));
      console.log('Cart saved to localStorage:', dataToSave.items.length, 'items');
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
    }
  },

  /**
   * Load cart data from localStorage
   */
  loadCart: (): PersistedCartData | null => {
    try {
      const storedData = localStorage.getItem(CART_STORAGE_KEY);
      
      if (!storedData) {
        return null;
      }

      const parsedData: PersistedCartData = JSON.parse(storedData);
      
      // Check if cart has expired
      const expiryTime = CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000; // Convert days to milliseconds
      const isExpired = Date.now() - parsedData.lastUpdated > expiryTime;
      
      if (isExpired) {
        console.log('Cart data expired, clearing localStorage');
        cartPersistenceService.clearCart();
        return null;
      }

      console.log('Cart loaded from localStorage:', parsedData.items.length, 'items');
      return parsedData;
    } catch (error) {
      console.error('Failed to load cart from localStorage:', error);
      // Clear corrupted data
      cartPersistenceService.clearCart();
      return null;
    }
  },

  /**
   * Clear cart data from localStorage
   */
  clearCart: (): void => {
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
      console.log('Cart cleared from localStorage');
    } catch (error) {
      console.error('Failed to clear cart from localStorage:', error);
    }
  },

  /**
   * Check if there's a persisted cart available
   */
  hasPersistedCart: (): boolean => {
    const cart = cartPersistenceService.loadCart();
    return cart !== null && cart.items.length > 0;
  },

  /**
   * Merge guest cart with user cart (for sign-in scenarios)
   * Guest cart takes precedence for items with same tire ID
   */
  mergeGuestCartWithUserCart: (
    guestCart: PersistedCartData,
    userCart: PersistedCartData | null
  ): PersistedCartData => {
    if (!userCart || userCart.items.length === 0) {
      return guestCart;
    }

    // Create a map of user cart items for easy lookup
    const userCartMap = new Map<string, CartItem>();
    userCart.items.forEach(item => {
      userCartMap.set(item.tire.id, item);
    });

    // Merge items, prioritizing guest cart quantities and adding user cart items not in guest cart
    const mergedItems: CartItem[] = [...guestCart.items];
    
    userCart.items.forEach(userItem => {
      const existsInGuestCart = guestCart.items.some(
        guestItem => guestItem.tire.id === userItem.tire.id
      );
      
      if (!existsInGuestCart) {
        mergedItems.push(userItem);
      }
    });

    // Use guest cart service type and location preferences, but fallback to user preferences
    return {
      items: mergedItems,
      service_type: guestCart.service_type || userCart.service_type,
      selected_location_id: guestCart.selected_location_id || userCart.selected_location_id,
      total: 0, // Will be recalculated by cart store
      lastUpdated: Date.now()
    };
  },

  /**
   * Get cart summary for logging/debugging
   */
  getCartSummary: (): string => {
    const cart = cartPersistenceService.loadCart();
    if (!cart) {
      return 'No persisted cart found';
    }

    const itemCount = cart.items.length;
    const totalQuantity = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    const lastUpdatedDate = new Date(cart.lastUpdated).toLocaleString();
    
    return `Cart: ${itemCount} item types, ${totalQuantity} total items, last updated: ${lastUpdatedDate}`;
  }
};
